import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { ArticleServicesServiceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ArticleServicesServiceFragmentFragmentDoc';
import { Box, Stack, Typography, Image, Checkbox, Dialog, Button } from '@packages/shared';
import { Checkmark32, InformationCircle32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { ArticleServicesCurrencyFragmentFragmentDoc } from '@packages/gql/generated/shopping/ArticleServicesCurrencyFragmentFragmentDoc';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks';
import { ServiceLayer } from '@packages/cms-components';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import { selectedServices } from '../../../atoms';
import { useArticleServicesTracking } from '../../Tracking/hooks/useArticleServicesTracking';
import { articleServicesMessages } from '../messages';
import { ImageReplacement } from '../../../helpers/ImageReplacement';

/* GraphQL */ `
  fragment ArticleServicesServiceFragment on Service {
    serviceIcon
    uuid
    name
    price
    gratis
    preselected
    mandatory
  }
`;
/* GraphQL */ `
  fragment ArticleServicesCurrencyFragment on ProductPrice {
    currency
  }
`;

export type ArticleServicesClusterProps = {
  data: FragmentType<typeof ArticleServicesServiceFragmentFragmentDoc>;
  /* should be in aPlus price value */
  currencyData: FragmentType<typeof ArticleServicesCurrencyFragmentFragmentDoc>;
};

export const ArticleServicesService = ({ data, currencyData }: ArticleServicesClusterProps) => {
  const { uuid, gratis, mandatory, serviceIcon, name, price, preselected } = getFragmentData(
    ArticleServicesServiceFragmentFragmentDoc,
    data,
  );
  const { currency } = getFragmentData(ArticleServicesCurrencyFragmentFragmentDoc, currencyData);

  const formatPrice = usePriceFormater('APLUS');
  const { formatMessage } = useIntl();

  const { trackingGlycerinDisplayLink, trackingGlycerinLinkClick } = useArticleServicesTracking();
  const { trackViewServiceLayer } = useLayerTracking();

  const [selected, setSelected] = useState(Boolean(preselected));
  const setAtomSelected = useSetAtom(selectedServices);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setAtomSelected((prev) =>
      selected
        ? prev.concat({ sku: uuid, selected: true })
        : prev.filter((service) => service.sku !== uuid),
    );
  }, [selected, setAtomSelected, uuid]);

  // initial tracking
  useEffect(() => {
    trackingGlycerinDisplayLink({
      category: uuid,
      detail: name || undefined,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMoreInfo = () => {
    trackingGlycerinLinkClick({
      category: uuid,
      detail: name || undefined,
    });
    trackViewServiceLayer(name);
    setIsModalOpen(true);
  };

  return (
    <>
      <Stack key={uuid} direction="row" alignItems="flex-start">
        <Box sx={{ width: '2.5rem', height: '1.75rem' }} display="flex" alignItems="center">
          {mandatory ? (
            <Checkmark32 fontSize="medium" color="success" aria-checked />
          ) : (
            <Checkbox
              clickTrackingProps={{
                category: uuid,
                detail: name || undefined,
                label: 'APlus Service',
              }}
              sx={{ padding: 0 }}
              checked={selected}
              onChange={() => setSelected((prev) => !prev)}
              id={uuid}
              aria-checked={selected}
            />
          )}
        </Box>

        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          flex={1}
          flexWrap="nowrap"
          justifyContent="flex-start"
        >
          <Image src={`${serviceIcon}.svg`} alt={name!} width={30} height={30} unoptimized />
          <Stack flex={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">
                {mandatory ? name : <label htmlFor={uuid}>{name}</label>}
              </Typography>

              {!gratis && price && (
                <Typography variant="body2" fontWeight="fontWeightSemiBold" whiteSpace="nowrap">
                  +{formatPrice(price, currency)}
                </Typography>
              )}
              {gratis && (
                <Typography
                  variant="body2"
                  fontWeight="fontWeightSemiBold"
                  sx={{ color: 'success.main' }}
                >
                  {mandatory
                    ? formatMessage(articleServicesMessages.articleServicesInclusive)
                    : formatMessage(articleServicesMessages.articleServicesFree)}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Button
            variant="text"
            onClick={handleMoreInfo}
            sx={{ '& svg': { width: '0.8em', height: '0.8em' } }}
          >
            <InformationCircle32 color="primary" />
            <ImageReplacement>
              {formatMessage(articleServicesMessages.articleServicesMoreInfo)}
            </ImageReplacement>
          </Button>
        </Stack>
      </Stack>
      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          title={name || ''}
          dialogContent={<ServiceLayer serviceLayerId={`a-plus_${uuid}`} />}
          displayTrackingProps={{
            category: uuid,
            detail: name || undefined,
            label: 'APlus Service',
          }}
        />
      )}
    </>
  );
};
